import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface ConfettiPiece {
  id: number;
  x: number;
  color: string;
  delay: number;
  rotation: number;
  scale: number;
}

interface ConfettiCelebrationProps {
  isActive: boolean;
  duration?: number;
  particleCount?: number;
}

const COLORS = [
  'hsl(42 34% 73%)',    // Gold
  'hsl(183 92% 51%)',   // Electric blue
  'hsl(182 37% 46%)',   // Teal
  'hsl(177 41% 76%)',   // Light aqua
  'hsl(45 93% 47%)',    // Bright yellow
  'hsl(340 82% 52%)',   // Pink
];

export default function ConfettiCelebration({ 
  isActive, 
  duration = 3000,
  particleCount = 50 
}: ConfettiCelebrationProps) {
  const [particles, setParticles] = useState<ConfettiPiece[]>([]);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isActive) {
      setShow(true);
      const newParticles: ConfettiPiece[] = [];
      
      for (let i = 0; i < particleCount; i++) {
        newParticles.push({
          id: i,
          x: Math.random() * 100,
          color: COLORS[Math.floor(Math.random() * COLORS.length)],
          delay: Math.random() * 0.5,
          rotation: Math.random() * 360,
          scale: 0.5 + Math.random() * 0.5,
        });
      }
      
      setParticles(newParticles);
      
      const timer = setTimeout(() => {
        setShow(false);
        setParticles([]);
      }, duration);
      
      return () => clearTimeout(timer);
    }
  }, [isActive, duration, particleCount]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      <AnimatePresence>
        {particles.map((particle) => (
          <motion.div
            key={particle.id}
            initial={{ 
              y: -20,
              x: `${particle.x}vw`,
              rotate: 0,
              opacity: 1,
              scale: 0
            }}
            animate={{ 
              y: '110vh',
              rotate: particle.rotation + 720,
              opacity: [1, 1, 0],
              scale: particle.scale
            }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 2.5 + Math.random(),
              delay: particle.delay,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
            className="absolute"
            style={{
              width: '12px',
              height: '12px',
            }}
          >
            {/* Confetti shapes */}
            {particle.id % 3 === 0 ? (
              <div 
                className="w-full h-full rounded-full"
                style={{ backgroundColor: particle.color }}
              />
            ) : particle.id % 3 === 1 ? (
              <div 
                className="w-full h-full"
                style={{ 
                  backgroundColor: particle.color,
                  clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)'
                }}
              />
            ) : (
              <div 
                className="w-3 h-1 rounded-full"
                style={{ backgroundColor: particle.color }}
              />
            )}
          </motion.div>
        ))}
      </AnimatePresence>
      
      {/* Central burst effect */}
      <motion.div
        initial={{ scale: 0, opacity: 1 }}
        animate={{ scale: 3, opacity: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full bg-gradient-radial from-primary/40 to-transparent"
      />
    </div>
  );
}
